import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", credentials: "", specialty: "", city: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await register(form);
    if (res.ok) {
      toast.success("Welcome to AzSAM.");
      navigate("/dashboard");
    } else {
      setError(res.error);
    }
    setLoading(false);
  };

  return (
    <div className="py-16" data-testid="register-page">
      <div className="max-w-lg mx-auto px-6">
        <div className="text-center">
          <div className="overline">Create your account</div>
          <h1 className="mt-4 font-heading text-4xl">Join the AzSAM community</h1>
        </div>
        <div className="mt-8 azsam-card p-7 bg-[color:var(--azsam-cream)]" data-testid="register-asam-notice">
          <div className="overline text-[color:var(--azsam-sage-hover)]">Membership requirement</div>
          <p className="mt-3 text-sm text-[color:var(--azsam-ink)] leading-relaxed">
            Membership in the Arizona Society of Addiction Medicine (AzSAM) requires active
            membership in the American Society of Addiction Medicine (ASAM). Please use the link
            below to learn more about ASAM membership and complete the enrollment process:
          </p>
          <a
            href="https://www.asam.org/membership/about-membership"
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[color:var(--azsam-clay)] hover:underline break-all"
            data-testid="register-asam-link"
          >
            asam.org/membership/about-membership →
          </a>
          <p className="mt-4 text-sm text-[color:var(--azsam-ink-soft)] leading-relaxed">
            Existing ASAM members may set up a member portal account below to register for events
            and view the clinician directory.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="mt-10 azsam-card p-8 space-y-5" data-testid="register-form">
          <F label="Full name" required>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="register-name" placeholder="Dr. Casey Rivera" />
          </F>
          <F label="Email" required>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="register-email" placeholder="you@clinic.org" />
          </F>
          <F label="Password" required>
            <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} data-testid="register-password" placeholder="At least 6 characters" />
          </F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Credentials">
              <input value={form.credentials} onChange={(e) => setForm({ ...form, credentials: e.target.value })} className={input} data-testid="register-credentials" placeholder="MD, DO, PhD…" />
            </F>
            <F label="City">
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={input} data-testid="register-city" placeholder="Phoenix" />
            </F>
          </div>
          <F label="Specialty">
            <input value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className={input} data-testid="register-specialty" placeholder="Addiction Medicine, Family Medicine…" />
          </F>
          {error && <div className="text-sm text-[color:var(--azsam-mesa)] bg-[color:var(--azsam-mesa)]/8 p-3 rounded-md" data-testid="register-error">{error}</div>}
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="register-submit">
            {loading ? "Creating account…" : "Create account"}
          </button>
          <div className="text-sm text-center text-[color:var(--azsam-ink-soft)]">
            Already have an account? <Link to="/login" className="text-[color:var(--azsam-clay)] font-medium hover:underline" data-testid="register-to-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const input = "w-full px-4 py-3 rounded-lg border border-[color:var(--azsam-stone)] focus:outline-none focus:border-[color:var(--azsam-clay)] transition";

function F({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-widest text-[color:var(--azsam-ink-soft)] mb-2 font-semibold">
        {label}{required && <span className="text-[color:var(--azsam-clay)]"> *</span>}
      </div>
      {children}
    </label>
  );
}
